import { useForm } from '@tanstack/react-form';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { CardContent, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  PasswordInput,
  TheButton,
  client,
} from '@virtual-care/ui';

import { auth } from '../firebase';

export default function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = z
    .object({
      currentPassword: z
        .string()
        .min(1, {
          message: t('auth.changePassword.validation.currentRequired'),
        }),
      password: z.string().min(8, {
        message: t('auth.changePassword.validation.passwordRequired'),
      }),
      confirmPassword: z.string().min(8, {
        message: t('auth.changePassword.validation.confirmRequired'),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.changePassword.validation.mismatch'),
      path: ['confirmPassword'],
    });

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        const issue = (field: string, message: string) =>
          ({ [field]: [{ message }] }) as Record<string, { message: string }[]>;

        const user = auth.currentUser;
        if (!user || !user.email) {
          return issue(
            'form',
            t('auth.changePassword.validation.genericError'),
          );
        }

        try {
          const credential = EmailAuthProvider.credential(
            user.email,
            props.value.currentPassword,
          );
          await reauthenticateWithCredential(user, credential);
        } catch {
          return issue(
            'currentPassword',
            t('auth.changePassword.validation.wrongCurrent'),
          );
        }

        try {
          await client.request('POST /me/password', {
            newPassword: props.value.password,
          });
        } catch (error) {
          const status =
            error && typeof error === 'object' && 'status' in error
              ? (error as { status: number }).status
              : 0;
          if (status === 400) {
            return issue(
              'password',
              t('auth.changePassword.validation.weakPassword'),
            );
          }
          if (status === 401) {
            return issue(
              'currentPassword',
              t('auth.changePassword.validation.wrongCurrent'),
            );
          }
          return issue('form', t('auth.changePassword.validation.syncError'));
        }

        try {
          const credential = await signInWithEmailAndPassword(
            auth,
            user.email,
            props.value.password,
          );
          await credential.user.getIdToken(true);
        } catch (error) {
          console.error(error);
          toast.success(t('auth.changePassword.reSignInRequired'));
          await signOut(auth);
          await navigate('/signin');
          return undefined;
        }

        await navigate('/');
        return undefined;
      },
    },
  });

  return (
    <AuthLayout
      center
      title={t('auth.changePassword.title')}
      subtitle={t('auth.changePassword.subtitle')}
    >
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="currentPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="currentPassword">
                  {t('auth.changePassword.currentPassword')}
                </Label>
                <PasswordInput
                  id="currentPassword"
                  dir="ltr"
                  autoComplete="current-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="password">
                  {t('auth.changePassword.newPassword')}
                </Label>
                <PasswordInput
                  id="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <p className="text-muted-foreground text-sm">
                  {t('auth.changePassword.hint')}
                </p>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirmPassword">
                  {t('auth.changePassword.confirmPassword')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  dir="ltr"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <TheButton
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                {t('auth.changePassword.submit')}
              </TheButton>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
